import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState, useLayoutEffect } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { Node, mergeAttributes } from "@tiptap/core";
import { Bold as BoldIcon, Italic as ItalicIcon, Underline as UnderlineIcon, Heading1, Heading2, Quote as QuoteIcon, Link as LinkIcon, Link2Off as LinkOffIcon, Image as ImageIcon, Minus as HrIcon, Video as VideoIcon, MoreHorizontal as MoreIcon, Loader2 as LoaderIcon, PlaySquare as PlaySquareIcon, } from "lucide-react";
// Minimal iframe node for embeds (e.g., YouTube/Vimeo)
const Iframe = Node.create({
    name: "iframe",
    group: "block",
    atom: true,
    selectable: true,
    draggable: true,
    addAttributes() {
        return {
            src: {
                default: null,
            },
            allow: {
                default: "accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share",
            },
            allowfullscreen: {
                default: true,
            },
            frameborder: {
                default: 0,
            },
            width: {
                default: "100%",
            },
            height: {
                default: 360,
            },
        };
    },
    parseHTML() {
        return [
            {
                tag: "iframe[src]",
            },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        return ["iframe", mergeAttributes(HTMLAttributes)];
    },
});
// Simple HTML5 video node for uploaded files
const Video = Node.create({
    name: "video",
    group: "block",
    atom: true,
    selectable: true,
    draggable: true,
    addAttributes() {
        return {
            src: {
                default: null,
            },
            controls: {
                default: true,
            },
            playsinline: {
                default: true,
            },
            muted: {
                default: false,
            },
            loop: {
                default: false,
            },
        };
    },
    parseHTML() {
        return [
            {
                tag: "video[src]",
            },
        ];
    },
    renderHTML({ HTMLAttributes }) {
        const attrs = mergeAttributes(HTMLAttributes);
        // boolean attrs
        if (attrs.controls === true)
            attrs.controls = "";
        if (attrs.playsinline === true)
            attrs.playsinline = "";
        if (attrs.muted === true)
            attrs.muted = "";
        if (attrs.loop === true)
            attrs.loop = "";
        return ["video", attrs];
    },
});
const toolbarButtonBaseClasses = "px-2 py-1 text-sm rounded border border-transparent text-wfGray-700 hover:bg-wfGray-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-wfGray-400 disabled:text-wfGray-300 disabled:hover:bg-transparent";
const getActiveButtonClasses = (isActive) => isActive ? " bg-wfGray-300 text-wfGray-900" : "";
const DropboxPaperEditor = ({ value, onChange, placeholder = "", className = "", toolbarClassName = "", contentClassName = "", uploadcareOptions, onAttachmentUploaded, emailSafe = false, }) => {
    const uploadcarePublicKey = process.env.REACT_APP_UPLOADCARE_PUBLIC_KEY;
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                // keep blockquote enabled by default
                bulletList: false,
                orderedList: false,
                listItem: false,
                codeBlock: false,
                code: false,
                heading: emailSafe ? false : {
                    levels: [1, 2],
                },
                // keep horizontalRule enabled by default
            }),
            Underline,
            Image,
            // Only allow embeds and videos if not in email-safe mode
            ...(emailSafe ? [] : [Iframe, Video]),
            Link.configure({
                openOnClick: false,
                linkOnPaste: true,
                HTMLAttributes: {
                    rel: "noopener noreferrer",
                    target: "_blank",
                },
            }),
        ],
        content: value !== null && value !== void 0 ? value : "",
        editorProps: {
            attributes: {
                "class": "ProseMirror min-h-[200px] outline-none focus:outline-none prose prose-sm max-w-none text-wfGray-900",
            },
        },
        onUpdate({ editor: instance }) {
            // Determine if content is effectively empty (e.g., <p></p>, empty marks)
            const hasMeaningfulContent = (ed) => {
                var _a, _b, _c, _d;
                const rawText = (((_a = ed.getText) === null || _a === void 0 ? void 0 : _a.call(ed)) || '').replace(/\u00a0/g, ' ').trim();
                if (rawText.length > 0)
                    return true;
                let found = false;
                try {
                    (_d = (_c = (_b = ed.state) === null || _b === void 0 ? void 0 : _b.doc) === null || _c === void 0 ? void 0 : _c.descendants) === null || _d === void 0 ? void 0 : _d.call(_c, (node) => {
                        var _a;
                        const typeName = (_a = node === null || node === void 0 ? void 0 : node.type) === null || _a === void 0 ? void 0 : _a.name;
                        if (typeName === 'image' || typeName === 'video' || typeName === 'iframe') {
                            found = true;
                        }
                        // prune further traversal into this node; not a global break
                        return false;
                    });
                }
                catch (_e) { }
                return found;
            };
            let html = instance.getHTML();
            const json = instance.getJSON();
            let text = instance.getText();
            if (!hasMeaningfulContent(instance)) {
                html = '';
                text = '';
            }
            onChange === null || onChange === void 0 ? void 0 : onChange({ html, json, text });
        },
    });
    const [uploadcareReady, setUploadcareReady] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const [showEmbedDialog, setShowEmbedDialog] = useState(false);
    const [embedUrlInput, setEmbedUrlInput] = useState("");
    useEffect(() => {
        if (!editor || value === undefined || value === null) {
            return;
        }
        const isStringContent = typeof value === "string";
        if (isStringContent) {
            if (value !== editor.getHTML()) {
                editor.commands.setContent(value, { emitUpdate: false });
            }
            return;
        }
        const currentJSON = editor.getJSON();
        const incomingJSON = value;
        if (JSON.stringify(currentJSON) !== JSON.stringify(incomingJSON)) {
            editor.commands.setContent(incomingJSON, { emitUpdate: false });
        }
    }, [editor, value]);
    useEffect(() => {
        if (!uploadcarePublicKey || typeof window === "undefined") {
            return;
        }
        let isMounted = true;
        setUploadcareReady(false);
        const assignPublicKey = () => {
            if (!isMounted) {
                return;
            }
            if (window.uploadcare) {
                window.uploadcare.publicKey = uploadcarePublicKey;
                window.UPLOADCARE_PUBLIC_KEY = uploadcarePublicKey;
                setUploadcareReady(true);
            }
        };
        if (window.uploadcare) {
            assignPublicKey();
            return () => {
                isMounted = false;
            };
        }
        const script = document.createElement("script");
        script.src = "https://ucarecdn.com/libs/widget/3.x/uploadcare.full.min.js";
        script.async = true;
        script.onload = assignPublicKey;
        document.body.appendChild(script);
        return () => {
            isMounted = false;
            if (script.parentElement) {
                script.parentElement.removeChild(script);
            }
        };
    }, [uploadcarePublicKey]);
    const setLink = useCallback(() => {
        if (!editor || typeof window === "undefined") {
            return;
        }
        const previousUrl = editor.getAttributes("link").href;
        const url = window.prompt("Set link", previousUrl !== null && previousUrl !== void 0 ? previousUrl : "");
        if (url === null) {
            return;
        }
        if (url === "") {
            editor.chain().focus().extendMarkRange("link").unsetLink().run();
            return;
        }
        editor
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: url, target: "_blank", rel: "noopener noreferrer" })
            .run();
    }, [editor]);
    const toEmbedUrl = (url) => {
        try {
            const u = new URL(url);
            const host = u.hostname.replace("www.", "");
            if (host === "youtube.com" || host === "m.youtube.com") {
                const id = u.searchParams.get("v");
                if (id)
                    return `https://www.youtube.com/embed/${id}`;
            }
            if (host === "youtu.be") {
                const id = u.pathname.replace("/", "");
                if (id)
                    return `https://www.youtube.com/embed/${id}`;
            }
            if (host === "vimeo.com") {
                const id = u.pathname.replace("/", "");
                if (id)
                    return `https://player.vimeo.com/video/${id}`;
            }
            return null;
        }
        catch (_a) {
            return null;
        }
    };
    const openEmbedDialog = useCallback(() => {
        setMoreOpen(false);
        setEmbedUrlInput("");
        setShowEmbedDialog(true);
    }, []);
    const insertEmbedFromUrl = useCallback(() => {
        if (!editor)
            return;
        const url = embedUrlInput.trim();
        if (!url) {
            setShowEmbedDialog(false);
            return;
        }
        const embed = toEmbedUrl(url);
        if (embed) {
            editor.chain().focus().insertContent({ type: "iframe", attrs: { src: embed } }).run();
        }
        else {
            editor.chain().focus().insertContent({ type: "video", attrs: { src: url } }).run();
        }
        setShowEmbedDialog(false);
    }, [editor, embedUrlInput]);
    const handleVideoUpload = useCallback(() => {
        if (!editor || typeof window === "undefined")
            return;
        if (!uploadcarePublicKey || (!uploadcareReady && !window.uploadcare))
            return;
        const uploadcare = window.uploadcare;
        const dialog = uploadcare.openDialog(null, Object.assign({ multiple: false, inputAcceptTypes: "video/*" }, uploadcareOptions));
        dialog.done((file) => {
            try {
                if (file && typeof file.progress === "function") {
                    setIsUploading(true);
                    setUploadProgress(0);
                    file.progress((info) => {
                        var _a;
                        try {
                            const p = Math.round(((_a = info === null || info === void 0 ? void 0 : info.progress) !== null && _a !== void 0 ? _a : info) * 100);
                            if (!Number.isNaN(p))
                                setUploadProgress(p);
                        }
                        catch (_b) { }
                    });
                }
                else {
                    setIsUploading(true);
                }
            }
            catch (_a) {
                setIsUploading(true);
            }
            file.done((fileInfo) => {
                const urlFromUpload = (fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.cdnUrl) || (fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.originalUrl);
                if (!urlFromUpload) {
                    setIsUploading(false);
                    return;
                }
                const mime = (fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.mimeType) || "";
                if (!mime.startsWith("video/")) {
                    setIsUploading(false);
                    window.alert("Please select a video file");
                    return;
                }
                editor.chain().focus().insertContent({ type: "video", attrs: { src: urlFromUpload } }).run();
                setIsUploading(false);
            });
            if (file && typeof file.fail === "function") {
                file.fail(() => setIsUploading(false));
            }
        });
    }, [editor, uploadcareOptions, uploadcarePublicKey, uploadcareReady]);
    const handleImageUpload = useCallback(() => {
        if (!editor || typeof window === "undefined") {
            return;
        }
        // Fallback if Uploadcare is not configured/ready: ask for URL
        if (!uploadcarePublicKey || !uploadcareReady || !window.uploadcare) {
            const url = window.prompt("Paste image URL");
            if (!url)
                return;
            editor.chain().focus().insertContent({ type: "image", attrs: { src: url } }).run();
            return;
        }
        const uploadcare = window.uploadcare;
        const dialog = uploadcare.openDialog(null, Object.assign({ imagesOnly: true, inputAcceptTypes: "image/*" }, uploadcareOptions));
        dialog.done((file) => {
            try {
                if (file && typeof file.progress === "function") {
                    setIsUploading(true);
                    setUploadProgress(0);
                    file.progress((info) => {
                        var _a;
                        try {
                            const p = Math.round(((_a = info === null || info === void 0 ? void 0 : info.progress) !== null && _a !== void 0 ? _a : info) * 100);
                            if (!Number.isNaN(p))
                                setUploadProgress(p);
                        }
                        catch (_b) { }
                    });
                }
                else {
                    setIsUploading(true);
                }
            }
            catch (_a) {
                setIsUploading(true);
            }
            file.done((fileInfo) => {
                const url = (fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.cdnUrl) || (fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.originalUrl);
                if (!url) {
                    setIsUploading(false);
                    return;
                }
                const alt = (fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.name) || (fileInfo === null || fileInfo === void 0 ? void 0 : fileInfo.originalFilename) || "Image";
                editor
                    .chain()
                    .focus()
                    .insertContent({ type: "image", attrs: { src: url, alt } })
                    .run();
                if (onAttachmentUploaded) {
                    onAttachmentUploaded(fileInfo);
                }
                setIsUploading(false);
            });
            if (file && typeof file.fail === "function") {
                file.fail(() => setIsUploading(false));
            }
        });
    }, [editor, uploadcareOptions, uploadcarePublicKey, uploadcareReady, onAttachmentUploaded]);
    const headingButtons = useMemo(() => [
        { level: 1, icon: _jsx(Heading1, { className: "h-4 w-4" }), label: "H1" },
        { level: 2, icon: _jsx(Heading2, { className: "h-4 w-4" }), label: "H2" },
    ], []);
    if (!editor) {
        return _jsx("div", { className: `border border-wfGray-200 rounded ${className}` });
    }
    // Marks: order Bold, Underline, Italic (per spec)
    const marksButtons = [
        {
            key: "bold",
            icon: _jsx(BoldIcon, { className: "h-4 w-4" }),
            active: editor.isActive("bold"),
            onClick: () => editor.chain().focus().toggleBold().run(),
            disabled: !editor.can().chain().focus().toggleBold().run(),
            label: "Bold",
        },
        {
            key: "underline",
            icon: _jsx(UnderlineIcon, { className: "h-4 w-4" }),
            active: editor.isActive("underline"),
            onClick: () => editor.chain().focus().toggleUnderline().run(),
            disabled: !editor.can().chain().focus().toggleUnderline().run(),
            label: "Underline",
        },
        {
            key: "italic",
            icon: _jsx(ItalicIcon, { className: "h-4 w-4" }),
            active: editor.isActive("italic"),
            onClick: () => editor.chain().focus().toggleItalic().run(),
            disabled: !editor.can().chain().focus().toggleItalic().run(),
            label: "Italic",
        },
    ];
    const blockButtons = [
        {
            key: "blockquote",
            icon: _jsx(QuoteIcon, { className: "h-4 w-4" }),
            active: editor.isActive("blockquote"),
            onClick: () => editor.chain().focus().toggleBlockquote().run(),
            disabled: !editor.can().chain().focus().toggleBlockquote().run(),
            label: "Blockquote",
        },
        {
            key: "hr",
            icon: _jsx(HrIcon, { className: "h-4 w-4" }),
            active: false,
            onClick: () => editor.chain().focus().setHorizontalRule().run(),
            disabled: !editor.can().chain().focus().setHorizontalRule().run(),
            label: "Horizontal rule",
        },
    ];
    // Toolbar grouping and overflow
    const containerRef = React.useRef(null);
    const moreButtonRef = React.useRef(null);
    const moreMenuRef = React.useRef(null);
    const embedDialogRef = React.useRef(null);
    const groupRefs = React.useRef({});
    const [overflowed, setOverflowed] = useState([]);
    const [moreOpen, setMoreOpen] = useState(false);
    const groupWidthsRef = React.useRef({});
    const groups = useMemo(() => {
        const base = [
            { key: "marks", priority: 1, render: () => marksButtons },
        ];
        if (!emailSafe) {
            base.push({ key: "headings", priority: 2, render: () => headingButtons });
        }
        base.push({
            key: "links",
            priority: 3,
            render: () => [
                {
                    key: "link",
                    icon: _jsx(LinkIcon, { className: "h-4 w-4" }),
                    active: editor.isActive("link"),
                    onClick: () => setLink(),
                    disabled: false,
                    label: "Link",
                },
                {
                    key: "unlink",
                    icon: _jsx(LinkOffIcon, { className: "h-4 w-4" }),
                    active: false,
                    onClick: () => editor.chain().focus().unsetLink().run(),
                    disabled: !editor.isActive("link"),
                    label: "Unlink",
                },
            ],
        });
        base.push({
            key: "media",
            priority: 4,
            render: () => {
                const items = [
                    {
                        key: "image",
                        icon: _jsx(ImageIcon, { className: "h-4 w-4" }),
                        active: false,
                        onClick: () => handleImageUpload(),
                        disabled: uploadcarePublicKey && !uploadcareReady ? true : false,
                        label: "Image",
                    },
                ];
                if (!emailSafe) {
                    items.push({
                        key: "video-embed",
                        icon: _jsx(PlaySquareIcon, { className: "h-4 w-4" }),
                        active: false,
                        onClick: () => openEmbedDialog(),
                        disabled: false,
                        label: "Embed video",
                    }, {
                        key: "video-upload",
                        icon: _jsx(VideoIcon, { className: "h-4 w-4" }),
                        active: false,
                        onClick: () => handleVideoUpload(),
                        disabled: !uploadcarePublicKey || (!uploadcareReady && !!uploadcarePublicKey),
                        label: "Upload video",
                    });
                }
                return items;
            },
        });
        base.push({ key: "blocks", priority: 5, render: () => blockButtons });
        return base;
    }, [editor, uploadcarePublicKey, uploadcareReady, headingButtons, emailSafe]);
    const recomputeOverflow = useCallback(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const el = containerRef.current;
        if (!el)
            return;
        const style = window.getComputedStyle(el);
        const gapPx = parseFloat((style.columnGap || style.gap || "0").toString()) || 0;
        const keysInOrder = groups
            .slice()
            .sort((a, b) => a.priority - b.priority)
            .map((g) => g.key);
        // Update cached widths (only if measurable)
        for (const key of keysInOrder) {
            const elRef = groupRefs.current[key];
            const w = elRef ? elRef.offsetWidth : 0;
            if (w && Math.abs((groupWidthsRef.current[key] || 0) - w) > 1) {
                groupWidthsRef.current[key] = w;
            }
        }
        // First, see if everything fits without a more button using cached widths
        let used = 0;
        let overflow = [];
        let placed = 0;
        for (const key of keysInOrder) {
            const w = (_c = (_a = groupWidthsRef.current[key]) !== null && _a !== void 0 ? _a : (_b = groupRefs.current[key]) === null || _b === void 0 ? void 0 : _b.offsetWidth) !== null && _c !== void 0 ? _c : 0;
            const addGap = placed > 0 ? gapPx : 0;
            if (used + addGap + w <= el.clientWidth) {
                used += addGap + w;
                placed += 1;
            }
            else {
                overflow.push(key);
            }
        }
        if (overflow.length === 0) {
            if (overflowed.length !== 0)
                setOverflowed([]);
            setMoreOpen(false);
            return;
        }
        // Recompute considering the more button width
        const moreW = (_e = (_d = moreButtonRef.current) === null || _d === void 0 ? void 0 : _d.offsetWidth) !== null && _e !== void 0 ? _e : 40;
        const available = Math.max(0, el.clientWidth - moreW - gapPx);
        used = 0;
        placed = 0;
        overflow = [];
        for (const key of keysInOrder) {
            const w = (_h = (_f = groupWidthsRef.current[key]) !== null && _f !== void 0 ? _f : (_g = groupRefs.current[key]) === null || _g === void 0 ? void 0 : _g.offsetWidth) !== null && _h !== void 0 ? _h : 0;
            const addGap = placed > 0 ? gapPx : 0;
            if (used + addGap + w <= available) {
                used += addGap + w;
                placed += 1;
            }
            else {
                overflow.push(key);
            }
        }
        // Only update state if changed to avoid thrash
        const changed = overflow.length !== overflowed.length || overflow.some((k, i) => k !== overflowed[i]);
        if (changed)
            setOverflowed(overflow);
        if (overflow.length === 0)
            setMoreOpen(false);
    }, [groups, overflowed]);
    useLayoutEffect(() => {
        recomputeOverflow();
        const el = containerRef.current;
        if (!el)
            return;
        let raf = 0;
        const ro = new ResizeObserver(() => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => recomputeOverflow());
        });
        ro.observe(el);
        return () => {
            ro.disconnect();
            cancelAnimationFrame(raf);
        };
    }, [recomputeOverflow]);
    // Close More on outside click or Escape
    useEffect(() => {
        if (!moreOpen)
            return;
        const onMouseDown = (e) => {
            const menu = moreMenuRef.current;
            const btn = moreButtonRef.current;
            const target = e.target;
            if (target && menu && menu.contains(target))
                return;
            if (target && btn && btn.contains(target))
                return;
            setMoreOpen(false);
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape')
                setMoreOpen(false);
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [moreOpen]);
    // Close Embed dialog on outside click or Escape
    useEffect(() => {
        if (!showEmbedDialog)
            return;
        const onMouseDown = (e) => {
            const modal = embedDialogRef.current;
            const target = e.target;
            if (target && modal && modal.contains(target))
                return;
            setShowEmbedDialog(false);
        };
        const onKeyDown = (e) => {
            if (e.key === 'Escape')
                setShowEmbedDialog(false);
            if (e.key === 'Enter')
                insertEmbedFromUrl();
        };
        document.addEventListener('mousedown', onMouseDown);
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('mousedown', onMouseDown);
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [showEmbedDialog, insertEmbedFromUrl]);
    return (_jsxs("div", { className: `border border-wfGray-300 rounded bg-white ${className}`, children: [_jsx("style", { children: `
          /* Ensure media is responsive and doesn't overflow editor width */
          .ProseMirror img { max-width: 100%; height: auto; }
          .ProseMirror video, .ProseMirror iframe { max-width: 100%; }
          /* Remove vertical margins on paragraphs */
          .ProseMirror p { margin-top: 0; margin-bottom: 0; }
        ` }), _jsxs("div", { ref: containerRef, className: `flex flex-nowrap items-center gap-2 border-b border-wfGray-200 px-3 py-2 ${toolbarClassName}`, children: [_jsx("div", { ref: (el) => (groupRefs.current["marks"] = el), className: `flex items-center gap-2${overflowed.includes("marks") ? " hidden" : ""}`, children: marksButtons.map((button) => (_jsxs("button", { type: "button", onClick: button.onClick, className: `${toolbarButtonBaseClasses}${getActiveButtonClasses(button.active)}`, disabled: button.disabled, children: [_jsx("span", { className: "sr-only", children: button.label }), button.icon] }, button.key))) }), !emailSafe ? (_jsx("div", { ref: (el) => (groupRefs.current["headings"] = el), className: `flex items-center gap-2 pl-3 border-l border-wfGray-200${overflowed.includes("headings") ? " hidden" : ""}`, children: headingButtons.map((heading) => (_jsxs("button", { type: "button", onClick: () => editor.chain().focus().toggleHeading({ level: heading.level }).run(), className: `${toolbarButtonBaseClasses}${getActiveButtonClasses(editor.isActive("heading", { level: heading.level }))}`, disabled: !editor.can().chain().focus().toggleHeading({ level: heading.level }).run(), children: [_jsx("span", { className: "sr-only", children: heading.label }), heading.icon] }, heading.level))) })) : null, _jsxs("div", { ref: (el) => (groupRefs.current["links"] = el), className: `flex items-center gap-2 pl-3 border-l border-wfGray-200${overflowed.includes("links") ? " hidden" : ""}`, children: [_jsxs("button", { type: "button", onClick: setLink, className: `${toolbarButtonBaseClasses}${getActiveButtonClasses(editor.isActive("link"))}`, children: [_jsx("span", { className: "sr-only", children: "Link" }), _jsx(LinkIcon, { className: "h-4 w-4" })] }), _jsxs("button", { type: "button", onClick: () => editor.chain().focus().unsetLink().run(), className: toolbarButtonBaseClasses, disabled: !editor.isActive("link"), children: [_jsx("span", { className: "sr-only", children: "Unlink" }), _jsx(LinkOffIcon, { className: "h-4 w-4" })] })] }), _jsxs("div", { ref: (el) => (groupRefs.current["media"] = el), className: `flex items-center gap-2 pl-3 border-l border-wfGray-200${overflowed.includes("media") ? " hidden" : ""}`, children: [_jsxs("button", { type: "button", onClick: handleImageUpload, className: `${toolbarButtonBaseClasses}${uploadcarePublicKey && !uploadcareReady ? " opacity-60" : ""}`, title: uploadcarePublicKey ? "Upload image" : "Insert image by URL", children: [_jsx("span", { className: "sr-only", children: "Image" }), _jsx(ImageIcon, { className: "h-4 w-4" })] }), !emailSafe ? (_jsxs("div", { className: "flex items-center gap-1", children: [_jsxs("button", { type: "button", onClick: openEmbedDialog, className: toolbarButtonBaseClasses, title: "Embed video from URL", children: [_jsx("span", { className: "sr-only", children: "Embed video from URL" }), _jsx(PlaySquareIcon, { className: "h-4 w-4" })] }), _jsxs("button", { type: "button", onClick: handleVideoUpload, className: toolbarButtonBaseClasses, title: "Upload video file", children: [_jsx("span", { className: "sr-only", children: "Upload video file" }), _jsx(VideoIcon, { className: "h-4 w-4" })] })] })) : null] }), _jsx("div", { ref: (el) => (groupRefs.current["blocks"] = el), className: `flex items-center gap-2 pl-3 border-l border-wfGray-200${overflowed.includes("blocks") ? " hidden" : ""}`, children: blockButtons.map((button) => (_jsxs("button", { type: "button", onClick: button.onClick, className: `${toolbarButtonBaseClasses}${getActiveButtonClasses(button.active)}`, disabled: button.disabled, children: [_jsx("span", { className: "sr-only", children: button.label }), button.icon] }, button.key))) }), overflowed.length > 0 && (_jsx("div", { className: "pl-3 border-l border-wfGray-200", children: _jsxs("button", { ref: moreButtonRef, type: "button", onClick: () => setMoreOpen((v) => !v), className: toolbarButtonBaseClasses, title: "More", children: [_jsx("span", { className: "sr-only", children: "More" }), _jsx(MoreIcon, { className: "h-4 w-4" })] }) }))] }), moreOpen && overflowed.length > 0 ? (_jsx("div", { className: "relative", children: _jsx("div", { ref: moreMenuRef, className: "absolute z-20 mt-1 rounded border border-wfGray-300 bg-white p-2 shadow", children: groups
                        .filter((g) => overflowed.includes(g.key))
                        .map((g) => (_jsx("div", { className: "flex items-center gap-2 py-1", children: g.render().map((item) => (_jsxs("button", { type: "button", onClick: () => { if (item.key === 'video-embed') {
                                openEmbedDialog();
                            }
                            else {
                                item.onClick();
                            } setMoreOpen(false); }, className: `${toolbarButtonBaseClasses}${item.active ? " bg-wfGray-300 text-wfGray-900" : ""}`, disabled: item.disabled, children: [_jsx("span", { className: "sr-only", children: item.label || item.level }), item.icon || null] }, item.key || item.level))) }, g.key))) }) })) : null, showEmbedDialog ? (_jsx("div", { className: "relative", children: _jsxs("div", { ref: embedDialogRef, className: "absolute z-30 left-3 top-3 right-3 rounded border border-wfGray-300 bg-white p-3 shadow", children: [_jsx("div", { className: "mb-2 text-sm text-wfGray-800", children: "Paste YouTube/Vimeo URL" }), _jsx("input", { type: "text", className: "w-full rounded border border-wfGray-300 px-2 py-1 text-sm", value: embedUrlInput, onChange: (e) => setEmbedUrlInput(e.target.value), placeholder: "https://www.youtube.com/watch?v=dQw4w9WgXcQ", onKeyDown: (e) => { if (e.key === 'Enter')
                                insertEmbedFromUrl(); }, autoFocus: true }), _jsxs("div", { className: "mt-2 flex justify-end gap-2", children: [_jsx("button", { type: "button", className: toolbarButtonBaseClasses, onClick: () => setShowEmbedDialog(false), children: "Cancel" }), _jsx("button", { type: "button", className: toolbarButtonBaseClasses, onClick: insertEmbedFromUrl, children: "Insert" })] })] }) })) : null, _jsxs("div", { className: `relative max-h-[60vh] overflow-y-auto ${contentClassName}`, children: [isUploading ? (_jsx("div", { className: "absolute inset-0 z-10 flex items-center justify-center bg-white/60", children: _jsxs("div", { className: "flex items-center gap-2 rounded border border-wfGray-300 bg-white px-3 py-2 text-sm text-wfGray-700", children: [_jsx(LoaderIcon, { className: "h-4 w-4 animate-spin" }), _jsxs("span", { children: ["Uploading", typeof uploadProgress === 'number' ? ` ${uploadProgress}%` : '', "\u2026"] })] }) })) : null, placeholder && editor.isEmpty && !editor.isFocused && (_jsx("span", { className: "pointer-events-none absolute left-4 top-4 text-sm text-wfGray-400", children: placeholder })), _jsx(EditorContent, { editor: editor, className: "px-3 py-3" })] })] }));
};
export default DropboxPaperEditor;
